import { FileResource } from "../../../modules/lipidcompass-backend-client";

/**
 * The client-side mirror of the server's {@code SubmissionDatasets} projection.
 *
 * <p>`submittedFiles` is a flat list that holds several artifacts of the SAME
 * dataset: the submitter's ORIGINAL bytes, the WORKING copy derived at upload,
 * and (from Milestone B) the CANONICAL copy derived at import. A component that
 * filters `fileType === "MZTAB_M"` therefore counts each dataset once per
 * derived artifact — listing every dataset twice in the wizard, offering the
 * ORIGINAL as a separately-validatable row, and double-counting it when
 * deciding whether the submission is complete.</p>
 *
 * <p>The server is authoritative: it resolves validation, preview, download and
 * import through its own projection regardless of what the client posts. This
 * exists so the UI SHOWS the same set of datasets the platform will act on.
 * Keep the precedence below in step with `SubmissionDatasets.SERVE_PRECEDENCE`.</p>
 */
const SERVE_PRECEDENCE: FileResource.RoleEnum[] = [
  FileResource.RoleEnum.CANONICAL,
  FileResource.RoleEnum.WORKING,
  FileResource.RoleEnum.ORIGINAL
];

/** A missing role reads as ORIGINAL — the semantics of every pre-feature file. */
export function roleOf(file: FileResource): FileResource.RoleEnum {
  return file?.role ?? FileResource.RoleEnum.ORIGINAL;
}

/** Which artifact of a single dataset is served: CANONICAL, else WORKING, else ORIGINAL. */
export function selectServableFile(
  files: FileResource[] | undefined
): FileResource | undefined {
  if (!files) {
    return undefined;
  }
  for (const wanted of SERVE_PRECEDENCE) {
    const match = files.find(f => f && roleOf(f) === wanted);
    if (match) {
      return match;
    }
  }
  return undefined;
}

/**
 * One artifact per mzTab-M dataset, in the order the datasets first appear.
 *
 * A file with no `datasetIndex` (legacy, pre-feature) is its own dataset and
 * appears exactly once — grouping on the index alone would collapse all such
 * files onto a single undefined key.
 */
export function datasetsOf(
  submittedFiles: FileResource[] | undefined
): FileResource[] {
  const files = submittedFiles ?? [];
  const datasets: FileResource[] = [];
  const alreadyEmitted = new Set<number>();
  for (const file of files) {
    if (!file || file.fileType !== FileResource.FileTypeEnum.MZTAB_M) {
      continue;
    }
    if (file.datasetIndex === null || file.datasetIndex === undefined) {
      datasets.push(file);
      continue;
    }
    if (alreadyEmitted.has(file.datasetIndex)) {
      continue;
    }
    alreadyEmitted.add(file.datasetIndex);
    const servable = selectServableFile(
      files.filter(f => f && f.datasetIndex === file.datasetIndex)
    );
    if (servable) {
      datasets.push(servable);
    }
  }
  return datasets;
}

/**
 * Every artifact that shares a dataset with `requested` — mirroring the server's
 * `SubmissionDatasets.siblingsOf`, which is the set deletion actually removes.
 *
 * <p>Matched by file name against the persisted list, so a caller holding a
 * stale row still resolves. A file with no `datasetIndex` has no siblings by
 * definition and is returned alone.</p>
 *
 * <p>Used to tell the submitter what a delete will really take with it. The
 * client must not act on this — the server recomputes it — but it must be able
 * to describe it accurately before asking for confirmation.</p>
 */
export function siblingsOf(
  submittedFiles: FileResource[] | undefined,
  requested: FileResource | undefined
): FileResource[] {
  if (!requested) {
    return [];
  }
  const files = submittedFiles ?? [];
  const persisted =
    files.find(f => f && f.fileName === requested.fileName) ?? requested;
  if (persisted.datasetIndex === null || persisted.datasetIndex === undefined) {
    return [persisted];
  }
  const siblings = files.filter(
    f => f && f.datasetIndex === persisted.datasetIndex
  );
  return siblings.length > 0 ? siblings : [persisted];
}

/**
 * The submission's file manifest: every non-mzTab file as-is, plus exactly one
 * artifact per mzTab-M dataset, in the original order.
 *
 * <p>For manifests that offer a DESTRUCTIVE action per row. Deletion cascades
 * over the whole dataset server-side — removing an ORIGINAL without the WORKING
 * copy derived from it would orphan bytes in storage — so listing both
 * artifacts as separate deletable rows presents the submitter with two rows that
 * each silently destroy the other. Showing one row per dataset makes the row and
 * the action agree.</p>
 *
 * <p>Manifests that are purely informational, or that offer a per-row DOWNLOAD,
 * should keep using `submittedFiles` directly: the ORIGINAL stays independently
 * retrievable for audit, which the server's `resolveForDownload` honours.</p>
 */
export function manifestOf(
  submittedFiles: FileResource[] | undefined
): FileResource[] {
  const files = submittedFiles ?? [];
  // datasetsOf returns references drawn from `files`, so identity membership is
  // exactly "is this artifact the one representing its dataset".
  const representatives = new Set<FileResource>(datasetsOf(files));
  return files.filter(
    file =>
      file &&
      (file.fileType !== FileResource.FileTypeEnum.MZTAB_M ||
        representatives.has(file))
  );
}
